import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ListTodo, CheckCircle2, RotateCcw, Trash2, Plus, Truck, Mail, FileText, MessageSquare, Send, ExternalLink, Clock } from "lucide-react";
import { usePolling } from "../lib/usePolling";
import { toast } from "sonner";

const SCOPES = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "overdue", label: "Overdue" },
  { key: "mine", label: "Assigned to me" },
  { key: "done", label: "Done" },
];

function todayStr() { return new Date().toISOString().slice(0, 10); }

export default function Reminders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scope, setScope] = useState("all");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", assignee: "", due_date: "", description: "", lr_number: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/garv/tasks?scope=${scope}`); setTasks(data); }
    catch { setTasks([]); }
    finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);
  usePolling(load, 15000, !showAdd);
  useEffect(() => { api.get("/garv/users").then((r) => setUsers(r.data || [])).catch(() => {}); }, []);

  async function toggleDone(t) {
    try { await api.patch(`/garv/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" }); load(); }
    catch { toast.error("Could not update"); }
  }
  async function remove(t) {
    if (!window.confirm(`Delete reminder "${t.title}"?`)) return;
    try { await api.delete(`/garv/tasks/${t.id}`); setTasks((x) => x.filter((y) => y.id !== t.id)); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  }
  async function addTask(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await api.post("/garv/tasks", form);
      toast.success("Reminder added");
      setForm({ title: "", assignee: "", due_date: "", description: "", lr_number: "" });
      setShowAdd(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add"); }
    finally { setSaving(false); }
  }

  const overdue = (t) => t.status === "open" && t.due_date && t.due_date < todayStr();

  return (
    <div data-testid="reminders-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Team Board</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1 flex items-center gap-2"><ListTodo className="w-8 h-8 text-[#C84B31]" /> Reminders</h1>
          <p className="text-sm text-[#68665E] mt-1">Everything GARV and the team have assigned — visible to everyone.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAdd((s) => !s)} data-testid="reminder-add-toggle"><Plus className="w-4 h-4" /> New Reminder</button>
      </div>

      {showAdd && (
        <form onSubmit={addTask} className="table-wrap p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="reminder-add-form">
          <div className="md:col-span-2"><label className="input-label">What needs doing?</label><input className="input-field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Arrange pickup for L&T job" data-testid="reminder-title" /></div>
          <div><label className="input-label">Assign to</label>
            <input list="reminder-users" className="input-field" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} placeholder="Name (staff or anyone)" data-testid="reminder-assignee" />
            <datalist id="reminder-users">{users.map((u) => <option key={u.id} value={u.name} />)}</datalist>
          </div>
          <div><label className="input-label">Due date</label><input type="date" className="input-field" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="reminder-due" /></div>
          <div><label className="input-label">Link LR number (optional)</label><input className="input-field" value={form.lr_number} onChange={(e) => setForm({ ...form, lr_number: e.target.value })} placeholder="e.g. 20260003" data-testid="reminder-lr" /></div>
          <div><label className="input-label">Notes (optional)</label><input className="input-field" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="reminder-desc" /></div>
          <div className="md:col-span-2 flex justify-end gap-2"><button type="button" className="btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button><button type="submit" className="btn-primary" disabled={saving} data-testid="reminder-save">{saving ? "Saving…" : "Add reminder"}</button></div>
        </form>
      )}

      <div className="flex flex-wrap gap-2 mb-5" data-testid="reminder-filters">
        {SCOPES.map((s) => (
          <button key={s.key} onClick={() => setScope(s.key)} data-testid={`reminder-filter-${s.key}`}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${scope === s.key ? "bg-[#C84B31] text-white" : "bg-[#E5E3DB] text-[#68665E] hover:text-[#1A1A18]"}`}>{s.label}</button>
        ))}
      </div>

      {loading ? <div className="text-sm text-[#68665E]">Loading…</div> : tasks.length === 0 ? (
        <div className="text-sm text-[#68665E]" data-testid="reminders-empty">No reminders here yet. Ask GARV (bottom-left) or click “New Reminder”.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="reminders-list">
          {tasks.map((t) => (
            <TaskCard key={t.id} t={t} overdue={overdue(t)} user={user} navigate={navigate} onToggle={() => toggleDone(t)} onRemove={() => remove(t)} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function TaskCard({ t, overdue, navigate, onToggle, onRemove, onChanged }) {
  const [comment, setComment] = useState("");
  const [showComments, setShowComments] = useState(false);
  const [posting, setPosting] = useState(false);

  async function postComment() {
    if (!comment.trim()) return;
    setPosting(true);
    try { await api.post(`/garv/tasks/${t.id}/comments`, { text: comment }); setComment(""); onChanged(); toast.success("Comment added"); }
    catch { toast.error("Could not comment"); }
    finally { setPosting(false); }
  }

  return (
    <div className={`table-wrap p-4 ${t.status === "done" ? "opacity-70" : ""}`} data-testid={`reminder-card-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${t.status === "done" ? "bg-[#E3F0E3] text-[#2F6F4E]" : overdue ? "bg-[#8A2526] text-white" : "bg-[#E5E3DB] text-[#68665E]"}`}>{t.status === "done" ? "Done" : overdue ? "Overdue" : "Open"}</span>
            {t.due_date && <span className="text-[11px] text-[#68665E] inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {t.due_date}</span>}
          </div>
          <div className={`font-medium text-[#1A1A18] ${t.status === "done" ? "line-through" : ""}`} data-testid={`reminder-title-${t.id}`}>{t.title}</div>
          {t.description && <div className="text-sm text-[#68665E] mt-0.5">{t.description}</div>}
          <div className="text-xs text-[#68665E] mt-1.5">
            <span className="font-medium text-[#C84B31]">{t.assignee_name || "Unassigned"}</span> · by {t.created_by_name}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onToggle} title={t.status === "done" ? "Reopen" : "Mark done"} className={t.status === "done" ? "text-[#68665E]" : "text-[#2F6F4E]"} data-testid={`reminder-toggle-${t.id}`}>{t.status === "done" ? <RotateCcw className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}</button>
          <button onClick={onRemove} title="Delete" className="text-[#8A2526]" data-testid={`reminder-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      {(t.links?.lr_number || t.links?.email_subject || t.links?.drive_file_name) && (
        <div className="flex flex-wrap gap-2 mt-3">
          {t.links.lr_number && <button onClick={() => t.links.lr_id && navigate(`/lr/${t.links.lr_id}`)} className="text-xs inline-flex items-center gap-1 bg-[#F5F4F0] px-2 py-1 rounded hover:text-[#C84B31]" data-testid={`reminder-link-lr-${t.id}`}><Truck className="w-3 h-3" /> {t.links.lr_number}</button>}
          {t.links.email_subject && <button onClick={() => navigate("/inbox")} className="text-xs inline-flex items-center gap-1 bg-[#F5F4F0] px-2 py-1 rounded hover:text-[#C84B31] max-w-[220px] truncate" data-testid={`reminder-link-email-${t.id}`}><Mail className="w-3 h-3 shrink-0" /> {t.links.email_subject}</button>}
          {t.links.drive_file_name && <span className="text-xs inline-flex items-center gap-1 bg-[#F5F4F0] px-2 py-1 rounded"><FileText className="w-3 h-3" /> {t.links.drive_file_name}</span>}
        </div>
      )}

      <button onClick={() => setShowComments((s) => !s)} className="text-xs text-[#68665E] hover:text-[#C84B31] mt-3 inline-flex items-center gap-1" data-testid={`reminder-comments-toggle-${t.id}`}>
        <MessageSquare className="w-3.5 h-3.5" /> {(t.comments?.length || 0)} comment{(t.comments?.length || 0) === 1 ? "" : "s"}
      </button>
      {showComments && (
        <div className="mt-2 space-y-2 border-t border-[#F0EDE5] pt-2" data-testid={`reminder-comments-${t.id}`}>
          {(t.comments || []).map((c) => (
            <div key={c.id} className="text-sm"><span className="font-medium text-[#1A1A18]">{c.author_name}:</span> <span className="text-[#4A4842]">{c.text}</span></div>
          ))}
          <div className="flex items-center gap-2">
            <input className="input-field flex-1 !py-1.5 text-sm" value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postComment()} placeholder="Add a note…" data-testid={`reminder-comment-input-${t.id}`} />
            <button onClick={postComment} disabled={posting || !comment.trim()} className="btn-primary !px-3 !py-1.5" data-testid={`reminder-comment-send-${t.id}`}><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
